import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { FichaCalendarioDetalhes, type CardapioDiaItem } from '../components/ficha/FichaCalendarioDetalhes'
import {
  FichaCatalogoTab,
  type CatalogoAjustarTarget,
} from '../components/ficha/FichaCatalogoTab'
import {
  ModalAplicarCardapio,
  type AplicarCardapioModo,
  type AplicarRefeicaoEscolha,
} from '../components/ficha/ModalAplicarCardapio'
import { PratosSection } from '../components/ficha/PratosSection'
import { descricaoCardapioDaSecao, secaoTemConteudo } from '../lib/descricaoCardapioFicha'
import { modalidadesDbDoGrupo } from '../lib/cardapioModalidade'
import {
  compararComReferencia,
  escopoJornadaDoTurno,
  labelRefeicao,
  nutrientesDaPorcao,
  refeicoesDoTurno,
  somarNutrientes,
  type ComparacaoStatus,
  type NutrientesTotais,
  type RefeicaoFicha,
} from '../lib/fichaTecnicaPacote'
import type { ModalidadeGrupoId } from '../lib/modalidadeGrupos'
import {
  alimentosSemTabelaPrecos,
  filtrarAlimentosTabelaPrecos,
  alimentoComTabelaPrecos,
  mensagemAlimentosIndisponiveis,
  opcoesAlimentoFicha,
} from '../lib/alimentosTabelaPrecos'
import {
  montarSecoesFichaPayload,
  salvarFichaTecnicaCompleta,
} from '../lib/salvarFichaTecnicaCompleta'
import { formatNumber } from '../lib/utils'
import { supabase } from '../lib/supabase'
import type {
  AlimentoMacronutriente,
  CalendarioDia,
  FichaTecnica,
  FichaTecnicaItem,
  FichaTecnicaPrato,
  ModalidadeEnsino,
  Prato,
  PratoItem,
  TurnoReferencia,
  ValorReferenciaRefeicao,
} from '../types/database'
import { TURNOS_REFERENCIA } from '../types/database'

type TabId = 'calendario' | 'catalogo' | 'ficha' | 'pratos'
type GrupoId = 'infantil' | 'fund1' | 'fund2' | 'eja'
type SlotKey = string

type Grupo = {
  id: GrupoId
  label: string
  modalidades: ModalidadeEnsino[]
  leitura: ModalidadeEnsino
  grupoCardapio: ModalidadeGrupoId
}

const GRUPOS: Grupo[] = [
  { id: 'infantil', label: 'Infantil', modalidades: ['pre'], leitura: 'pre', grupoCardapio: 'infantil' },
  { id: 'fund1', label: 'Fundamental Menor', modalidades: ['fund1'], leitura: 'fund1', grupoCardapio: 'fund1' },
  { id: 'fund2', label: 'Fundamental Maior', modalidades: ['fund2'], leitura: 'fund2', grupoCardapio: 'fund2' },
  { id: 'eja', label: 'EJA', modalidades: ['eja'], leitura: 'eja', grupoCardapio: 'eja' },
]

const MES_LABELS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

type ItemDraft = {
  key: string
  alimento_id: string
  quantidade_g: string
  origem_prato_id: string | null
}

type SecaoDraft = {
  refeicao: RefeicaoFicha
  fichaId?: string
  descritivo: string
  /** Pratos aplicados (histórico / auditoria); totais vêm de `itens`. */
  pratoIds: string[]
  itens: ItemDraft[]
}

function newItemKey() {
  return `tmp-${Math.random().toString(36).slice(2, 9)}`
}

function emptyItem(origem_prato_id: string | null = null): ItemDraft {
  return { key: newItemKey(), alimento_id: '', quantidade_g: '0', origem_prato_id }
}

type DiaLetivoUi = CalendarioDia & {
  dia_semana: number
  slotKey: SlotKey
  apto: boolean
}

function isoDow(iso: string): number {
  const js = new Date(`${iso}T12:00:00`).getDay()
  return js === 0 ? 7 : js
}

function slotKeyOf(trimestre: number, semana: number, diaSemana: number): SlotKey {
  return `${trimestre}:${semana}:${diaSemana}`
}

function statusClass(s: ComparacaoStatus): string {
  if (s === 'ok') return 'ficha-cmp-ok'
  if (s === 'abaixo') return 'ficha-cmp-abaixo'
  if (s === 'acima') return 'ficha-cmp-acima'
  return 'ficha-cmp-sem'
}

function statusLabel(s: ComparacaoStatus): string {
  if (s === 'ok') return 'Dentro'
  if (s === 'abaixo') return 'Abaixo'
  if (s === 'acima') return 'Acima'
  return 'Sem meta'
}

const NUTRI_COLS: { key: keyof NutrientesTotais; label: string }[] = [
  { key: 'energia_kcal', label: 'Energia kcal' },
  { key: 'energia_kj', label: 'Energia kJ' },
  { key: 'carboidratos_g', label: 'Carboidratos (g)' },
  { key: 'proteinas_g', label: 'Proteínas (g)' },
  { key: 'lipideos_g', label: 'Lipídeos (g)' },
  { key: 'vitamina_a_mcg', label: 'Vitamina A (mcg)' },
  { key: 'vitamina_c_g', label: 'Vitamina C (g)' },
  { key: 'calcio_mg', label: 'Cálcio (mg)' },
  { key: 'ferro_mg', label: 'Ferro (mg)' },
]

export function FichaTecnicaPage() {
  const { canWrite, canDelete } = useAuth()
  const writable = canWrite('ficha_tecnica')
  const writableCardapio = canWrite('cardapio')
  const canExcluirFicha = canDelete('ficha_tecnica')

  const [tab, setTab] = useState<TabId>('calendario')
  const [grupoId, setGrupoId] = useState<GrupoId>('infantil')
  const [turno, setTurno] = useState<TurnoReferencia>('manha')
  /** Flag da modalidade atual: Integral com cardápio próprio. */
  const [integralIndependente, setIntegralIndependente] = useState(false)
  const [togglingIntegral, setTogglingIntegral] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [pratos, setPratos] = useState<Prato[]>([])
  const [pratoItens, setPratoItens] = useState<PratoItem[]>([])
  const [secoes, setSecoes] = useState<SecaoDraft[]>([])
  const [referencia, setReferencia] = useState<ValorReferenciaRefeicao | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [cursor, setCursor] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [diasMes, setDiasMes] = useState<CalendarioDia[]>([])
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [diasComCardapio, setDiasComCardapio] = useState<Set<string>>(new Set())
  const [nutricionistaResponsavel, setNutricionistaResponsavel] = useState('')
  const [applying, setApplying] = useState(false)
  const [focusedDate, setFocusedDate] = useState<string | null>(null)
  const [cardapiosDia, setCardapiosDia] = useState<CardapioDiaItem[]>([])
  const [loadingCardapioDia, setLoadingCardapioDia] = useState(false)
  const [modalApplyOpen, setModalApplyOpen] = useState(false)
  const [applyModo, setApplyModo] = useState<AplicarCardapioModo>('dia')

  const grupo = useMemo(() => GRUPOS.find((g) => g.id === grupoId) || GRUPOS[0], [grupoId])
  const year = cursor.getFullYear()
  const month = cursor.getMonth()
  const turnoMeta = useMemo(() => TURNOS_REFERENCIA.find((t) => t.value === turno), [turno])
  const escopoJornada = useMemo(
    () => escopoJornadaDoTurno(turno, integralIndependente),
    [turno, integralIndependente],
  )

  const loadIntegralFlag = useCallback(async () => {
    const { data, error: err } = await supabase
      .from('modalidade_cardapio_config')
      .select('cardapio_integral_independente')
      .eq('modalidade', grupo.leitura)
      .maybeSingle()
    if (err) {
      setError(err.message)
      setIntegralIndependente(false)
      return
    }
    setIntegralIndependente(!!data?.cardapio_integral_independente)
  }, [grupo.leitura])

  const alimentosById = useMemo(() => {
    const m = new Map<string, AlimentoMacronutriente>()
    for (const a of alimentos) m.set(a.id, a)
    return m
  }, [alimentos])

  const alimentosCatalogo = useMemo(() => filtrarAlimentosTabelaPrecos(alimentos), [alimentos])

  const alimentosIndisponiveis = useMemo(() => {
    const ids = secoes.flatMap((s) => s.itens.map((it) => it.alimento_id))
    return alimentosSemTabelaPrecos(ids, alimentosById)
  }, [secoes, alimentosById])

  const avisoIndisponiveis = useMemo(
    () => mensagemAlimentosIndisponiveis(alimentosIndisponiveis.map((a) => a.nome)),
    [alimentosIndisponiveis],
  )

  const itensByPrato = useMemo(() => {
    const m = new Map<string, PratoItem[]>()
    for (const it of pratoItens) {
      const list = m.get(it.prato_id) || []
      list.push(it)
      m.set(it.prato_id, list)
    }
    return m
  }, [pratoItens])

  const nutrientesPrato = useCallback(
    (pratoId: string): NutrientesTotais => {
      const itens = itensByPrato.get(pratoId) || []
      return somarNutrientes(
        itens.map((it) => nutrientesDaPorcao(alimentosById.get(it.alimento_id), Number(it.quantidade_g) || 0)),
      )
    },
    [itensByPrato, alimentosById],
  )

  const loadCatalog = useCallback(async () => {
    const [alRes, prRes, itRes, cfgRes] = await Promise.all([
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
      supabase.from('pratos').select('*').order('nome'),
      supabase.from('prato_itens').select('*').order('ordem'),
      supabase.from('configuracoes').select('nutricionista_responsavel').eq('id', 1).maybeSingle(),
    ])
    if (alRes.error) setError(alRes.error.message)
    else setAlimentos((alRes.data as AlimentoMacronutriente[]) || [])
    if (prRes.error) setError(prRes.error.message)
    else setPratos((prRes.data as Prato[]) || [])
    if (itRes.error) setError(itRes.error.message)
    else setPratoItens((itRes.data as PratoItem[]) || [])
    setNutricionistaResponsavel(cfgRes.data?.nutricionista_responsavel ?? '')
  }, [])

  const loadFicha = useCallback(async () => {
    setLoading(true)
    setError(null)
    const refs = refeicoesDoTurno(turno)
    const escopo = escopoJornadaDoTurno(turno, integralIndependente)
    const [fichasRes, refRes] = await Promise.all([
      supabase
        .from('fichas_tecnicas')
        .select('*')
        .eq('modalidade', grupo.leitura)
        .eq('escopo_jornada', escopo)
        .in('refeicao', refs),
      supabase
        .from('valores_referencia_refeicao')
        .select('*')
        .eq('modalidade', grupo.leitura)
        .eq('turno', turno)
        .maybeSingle(),
    ])
    if (fichasRes.error) {
      setError(fichasRes.error.message)
      setLoading(false)
      return
    }
    if (refRes.error) setError(refRes.error.message)
    setReferencia((refRes.data as ValorReferenciaRefeicao) || null)

    const fichas = (fichasRes.data as FichaTecnica[]) || []
    const fichaIds = fichas.map((f) => f.id)
    let links: FichaTecnicaPrato[] = []
    let itensDb: FichaTecnicaItem[] = []
    if (fichaIds.length) {
      const [linkRes, itensRes] = await Promise.all([
        supabase.from('ficha_tecnica_pratos').select('*').in('ficha_id', fichaIds).order('ordem'),
        supabase.from('ficha_tecnica_itens').select('*').in('ficha_id', fichaIds).order('ordem'),
      ])
      if (linkRes.error) {
        setError(linkRes.error.message)
        setLoading(false)
        return
      }
      if (itensRes.error) {
        setError(itensRes.error.message)
        setLoading(false)
        return
      }
      links = (linkRes.data as FichaTecnicaPrato[]) || []
      itensDb = (itensRes.data as FichaTecnicaItem[]) || []
    }

    setSecoes(
      refs.map((refeicao) => {
        const ficha = fichas.find((f) => f.refeicao === refeicao)
        const pratoIds = ficha
          ? links.filter((l) => l.ficha_id === ficha.id).map((l) => l.prato_id)
          : []
        const itens = ficha
          ? itensDb
              .filter((it) => it.ficha_id === ficha.id)
              .map((it) => ({
                key: it.id,
                alimento_id: it.alimento_id,
                quantidade_g: String(it.quantidade_g ?? 0),
                origem_prato_id: it.origem_prato_id ?? null,
              }))
          : []
        return {
          refeicao,
          fichaId: ficha?.id,
          descritivo: ficha?.descritivo || '',
          pratoIds,
          itens,
        }
      }),
    )
    setLoading(false)
  }, [grupo.leitura, turno, integralIndependente])

  const loadCalendario = useCallback(async () => {
    const start = `${year}-${String(month + 1).padStart(2, '0')}-01`
    const endDate = new Date(year, month + 1, 0)
    const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
    const { data, error: err } = await supabase
      .from('calendario')
      .select('*')
      .gte('data', start)
      .lte('data', end)
    if (err) setError(err.message)
    else setDiasMes((data as CalendarioDia[]) || [])
  }, [year, month])

  const marcarDiasComCardapio = useCallback(async () => {
    const letivos = diasMes.filter((d) => d.tipo === 'letivo')
    if (!letivos.length) {
      setDiasComCardapio(new Set())
      return
    }
    const mods = modalidadesDbDoGrupo(grupo.grupoCardapio)
    const refs = refeicoesDoTurno(turno)
    const escopo = escopoJornadaDoTurno(turno, integralIndependente)
    const { data } = await supabase
      .from('cardapios')
      .select('trimestre,semana,dia_semana,descricao')
      .eq('ano', year)
      .eq('escopo_jornada', escopo)
      .in('modalidade', mods)
      .in('refeicao', refs)
    const slotsComDesc = new Set(
      ((data as { trimestre: number; semana: number; dia_semana: number; descricao: string }[]) || [])
        .filter((c) => (c.descricao || '').trim())
        .map((c) => slotKeyOf(c.trimestre, c.semana, c.dia_semana)),
    )
    const marked = new Set<string>()
    for (const d of letivos) {
      const dow = isoDow(d.data)
      if (
        d.trimestre != null &&
        d.semana_cardapio != null &&
        dow >= 1 &&
        dow <= 5 &&
        slotsComDesc.has(slotKeyOf(Number(d.trimestre), Number(d.semana_cardapio), dow))
      ) {
        marked.add(d.data)
      }
    }
    setDiasComCardapio(marked)
  }, [diasMes, grupo.grupoCardapio, turno, year, integralIndependente])

  useEffect(() => {
    document.title = 'Ficha técnica · ISEN EDUCAE'
  }, [])

  useEffect(() => {
    void loadIntegralFlag()
  }, [loadIntegralFlag])

  useEffect(() => {
    if (tab === 'pratos' || tab === 'catalogo') return
    void loadCatalog()
    void loadFicha()
  }, [tab, loadCatalog, loadFicha])

  function irParaAjusteCatalogo(target: CatalogoAjustarTarget) {
    setGrupoId(target.grupoId)
    setTurno(target.turno)
    setTab('ficha')
    setInfo(
      `Abrindo pacote ${target.grupoId} · ${target.turno} para ajustar ${labelRefeicao(target.refeicao)}.`,
    )
  }

  useEffect(() => {
    void loadCalendario()
    setSelectedDates([])
    setFocusedDate(null)
  }, [loadCalendario])

  // Nova modalidade/turno = outro contexto de cardápio: limpa lote e foco visual.
  useEffect(() => {
    setSelectedDates([])
    setFocusedDate(null)
  }, [grupoId, turno])

  useEffect(() => {
    void marcarDiasComCardapio()
  }, [marcarDiasComCardapio])

  const letivoByDate = useMemo(() => {
    const map = new Map<string, DiaLetivoUi>()
    for (const d of diasMes) {
      if (d.tipo !== 'letivo') continue
      const dia_semana = isoDow(d.data)
      const apto =
        d.trimestre != null &&
        d.semana_cardapio != null &&
        dia_semana >= 1 &&
        dia_semana <= 5
      map.set(d.data, {
        ...d,
        dia_semana,
        apto,
        slotKey: apto
          ? slotKeyOf(Number(d.trimestre), Number(d.semana_cardapio), dia_semana)
          : '',
      })
    }
    return map
  }, [diasMes])

  const selectedSlots = useMemo(() => {
    const map = new Map<SlotKey, { trimestre: number; semana: number; dia_semana: number }>()
    for (const iso of selectedDates) {
      const d = letivoByDate.get(iso)
      if (!d?.apto) continue
      map.set(d.slotKey, {
        trimestre: Number(d.trimestre),
        semana: Number(d.semana_cardapio),
        dia_semana: d.dia_semana,
      })
    }
    return [...map.values()]
  }, [selectedDates, letivoByDate])

  const cells = useMemo(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDow = new Date(year, month, 1).getDay()
    const result: { day: number | null; iso?: string; letivo?: DiaLetivoUi }[] = []
    for (let i = 0; i < firstDow; i++) result.push({ day: null })
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      result.push({ day: d, iso, letivo: letivoByDate.get(iso) })
    }
    return result
  }, [year, month, letivoByDate])

  useEffect(() => {
    if (focusedDate && letivoByDate.has(focusedDate)) return
    const first = [...letivoByDate.values()].find((d) => d.apto)
    if (first) setFocusedDate(first.data)
  }, [letivoByDate, focusedDate])

  const loadCardapiosDia = useCallback(async () => {
    if (!focusedDate) {
      setCardapiosDia([])
      return
    }
    const dia = letivoByDate.get(focusedDate)
    if (!dia?.apto) {
      setCardapiosDia([])
      return
    }
    setLoadingCardapioDia(true)
    const mods = modalidadesDbDoGrupo(grupo.grupoCardapio)
    const refs = refeicoesDoTurno(turno)
    const escopo = escopoJornadaDoTurno(turno, integralIndependente)
    const { data, error: err } = await supabase
      .from('cardapios')
      .select('refeicao, descricao, modalidade, prato_id')
      .eq('ano', year)
      .eq('trimestre', dia.trimestre!)
      .eq('semana', dia.semana_cardapio!)
      .eq('dia_semana', dia.dia_semana)
      .eq('escopo_jornada', escopo)
      .in('modalidade', mods)
      .in('refeicao', refs)
    if (err) setError(err.message)
    else {
      type RowDia = {
        refeicao: RefeicaoFicha
        descricao: string
        modalidade: ModalidadeEnsino
        prato_id: string | null
      }
      const rows = (data as RowDia[]) || []
      const byRef = new Map<RefeicaoFicha, { descricao: string; pratoNome: string | null }>()
      for (const ref of refs) {
        const prefer = rows.find((r) => r.refeicao === ref && r.modalidade === grupo.leitura)
        const any = rows.find((r) => r.refeicao === ref && (r.descricao || '').trim())
        const row = prefer || any
        const desc = (row?.descricao || '').trim()
        if (!desc) continue
        const pratoNome = row?.prato_id
          ? pratos.find((p) => p.id === row.prato_id)?.nome || null
          : null
        byRef.set(ref, { descricao: desc, pratoNome })
      }
      setCardapiosDia(
        refs
          .filter((r) => byRef.has(r))
          .map((refeicao) => ({
            refeicao,
            descricao: byRef.get(refeicao)!.descricao,
            pratoNome: byRef.get(refeicao)!.pratoNome,
          })),
      )
    }
    setLoadingCardapioDia(false)
  }, [
    focusedDate,
    letivoByDate,
    grupo.grupoCardapio,
    grupo.leitura,
    turno,
    year,
    pratos,
    integralIndependente,
  ])

  useEffect(() => {
    if (tab !== 'calendario') return
    void loadCardapiosDia()
  }, [tab, loadCardapiosDia])

  async function toggleIntegralIndependente(checked: boolean) {
    if (!writable && !writableCardapio) return
    setTogglingIntegral(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: err } = await supabase.rpc('set_cardapio_integral_independente', {
        p_modalidade: grupo.leitura,
        p_independente: checked,
      })
      if (err) throw new Error(err.message)
      setIntegralIndependente(checked)
      const row = data as { fichas_copiadas?: number; cardapios_copiados?: number } | null
      if (checked) {
        setInfo(
          `Cardápio integral independente ativado` +
            (row?.fichas_copiadas || row?.cardapios_copiados
              ? ` (copiados ${row.fichas_copiadas || 0} ficha(s) e ${row.cardapios_copiados || 0} dia(s) do parcial).`
              : '.'),
        )
      } else {
        setInfo(
          'Cardápio integral independente desativado. O turno Integral volta a usar o cardápio parcial.',
        )
      }
      if (turno === 'integral') {
        await loadFicha()
        await marcarDiasComCardapio()
        await loadCardapiosDia()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao alterar flag do Integral')
    } finally {
      setTogglingIntegral(false)
    }
  }

  function focarDia(iso: string) {
    const dia = letivoByDate.get(iso)
    if (!dia) return
    if (!dia.apto) {
      setError('Este dia letivo ainda não tem trimestre/semana de cardápio no calendário.')
      return
    }
    setError(null)
    setFocusedDate(iso)
    setSelectedDates((prev) =>
      prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort(),
    )
  }

  function toggleFocusedInBatch(checked: boolean) {
    if (!focusedDate) return
    setSelectedDates((prev) => {
      if (checked) return prev.includes(focusedDate) ? prev : [...prev, focusedDate].sort()
      return prev.filter((x) => x !== focusedDate)
    })
  }

  function selecionarTodosLetivos() {
    const aptos = [...letivoByDate.values()].filter((d) => d.apto).map((d) => d.data)
    setSelectedDates(aptos.sort())
  }

  function limparSelecao() {
    setSelectedDates([])
  }

  function adicionarPrato(refeicao: RefeicaoFicha, pratoId: string) {
    if (!pratoId) return
    const fonte = itensByPrato.get(pratoId) || []
    setSecoes((prev) =>
      prev.map((s) => {
        if (s.refeicao !== refeicao) return s
        const copias: ItemDraft[] = fonte.map((it) => ({
          key: newItemKey(),
          alimento_id: it.alimento_id,
          quantidade_g: String(it.quantidade_g ?? 0),
          origem_prato_id: pratoId,
        }))
        return {
          ...s,
          pratoIds: s.pratoIds.includes(pratoId) ? s.pratoIds : [...s.pratoIds, pratoId],
          itens: [...s.itens, ...copias],
        }
      }),
    )
  }

  function updateItem(
    refeicao: RefeicaoFicha,
    key: string,
    patch: Partial<Pick<ItemDraft, 'alimento_id' | 'quantidade_g'>>,
  ) {
    setSecoes((prev) =>
      prev.map((s) => {
        if (s.refeicao !== refeicao) return s
        return {
          ...s,
          itens: s.itens.map((it) => (it.key === key ? { ...it, ...patch } : it)),
        }
      }),
    )
  }

  function removeItem(refeicao: RefeicaoFicha, key: string) {
    setSecoes((prev) =>
      prev.map((s) => {
        if (s.refeicao !== refeicao) return s
        return { ...s, itens: s.itens.filter((it) => it.key !== key) }
      }),
    )
  }

  function addItemManual(refeicao: RefeicaoFicha) {
    setSecoes((prev) =>
      prev.map((s) => (s.refeicao === refeicao ? { ...s, itens: [...s.itens, emptyItem()] } : s)),
    )
  }

  const totaisPorSecao = useMemo(() => {
    const map = new Map<RefeicaoFicha, NutrientesTotais>()
    for (const s of secoes) {
      map.set(
        s.refeicao,
        somarNutrientes(
          s.itens.map((it) =>
            nutrientesDaPorcao(alimentosById.get(it.alimento_id), Number(it.quantidade_g) || 0),
          ),
        ),
      )
    }
    return map
  }, [secoes, alimentosById])

  const totalPacote = useMemo(
    () => somarNutrientes([...totaisPorSecao.values()]),
    [totaisPorSecao],
  )

  const comparacao = useMemo(
    () => compararComReferencia(totalPacote, referencia),
    [totalPacote, referencia],
  )

  function descricaoDaSecao(s: SecaoDraft): string {
    return descricaoCardapioDaSecao(s, pratos, alimentosById)
  }

  const slotsFromDates = useCallback(
    (dates: string[]) => {
      const map = new Map<SlotKey, { trimestre: number; semana: number; dia_semana: number }>()
      for (const iso of dates) {
        const d = letivoByDate.get(iso)
        if (!d?.apto) continue
        map.set(d.slotKey, {
          trimestre: Number(d.trimestre),
          semana: Number(d.semana_cardapio),
          dia_semana: d.dia_semana,
        })
      }
      return [...map.values()]
    },
    [letivoByDate],
  )

  async function aplicarComRefeicoes(escolhas: AplicarRefeicaoEscolha[], targetDates: string[]) {
    if (!writableCardapio) return
    const slots = slotsFromDates(targetDates)
    if (!slots.length) {
      setError('Nenhum dia letivo válido selecionado (T/S obrigatório).')
      return
    }
    if (!escolhas.length) {
      setError('Selecione ao menos uma refeição com prato (quando houver).')
      return
    }
    setApplying(true)
    setError(null)
    setInfo(null)
    const mods = modalidadesDbDoGrupo(grupo.grupoCardapio)
    try {
      for (const slot of slots) {
        for (const escolha of escolhas) {
          const secao = secoes.find((s) => s.refeicao === escolha.refeicao)
          if (!secao || !secaoTemConteudo(secao)) continue
          const secaoAplicar: SecaoDraft = escolha.pratoId
            ? {
                ...secao,
                pratoIds: [escolha.pratoId],
                itens: secao.itens.filter((it) => it.origem_prato_id === escolha.pratoId),
              }
            : secao
          const descricao = descricaoDaSecao(secaoAplicar)
          for (const modalidade of mods) {
            const { error: err } = await supabase.from('cardapios').upsert(
              {
                modalidade,
                trimestre: slot.trimestre,
                semana: slot.semana,
                dia_semana: slot.dia_semana,
                refeicao: secao.refeicao,
                descricao,
                prato_id: escolha.pratoId,
                nutricionista_responsavel: nutricionistaResponsavel || '',
                ano: year,
                escopo_jornada: escopoJornada,
              },
              {
                onConflict:
                  'modalidade,trimestre,semana,dia_semana,refeicao,ano,escopo_jornada',
              },
            )
            if (err) throw new Error(err.message)
          }
        }
      }
      setInfo(
        `Cardápio atualizado em ${targetDates.length} dia(s) · ${escolhas.length} refeição(ões) · ${slots.length} slot(s) T/S.`,
      )
      await marcarDiasComCardapio()
      await loadCardapiosDia()
      setModalApplyOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao aplicar no calendário')
    } finally {
      setApplying(false)
    }
  }

  async function limparCardapioSlots(
    dates: string[],
    refeicoes: RefeicaoFicha[] = refeicoesDoTurno(turno),
  ) {
    if (!writableCardapio) return
    const slots = slotsFromDates(dates)
    if (!slots.length) return
    setApplying(true)
    setError(null)
    const mods = modalidadesDbDoGrupo(grupo.grupoCardapio)
    try {
      for (const slot of slots) {
        const { error: err } = await supabase
          .from('cardapios')
          .delete()
          .eq('ano', year)
          .eq('trimestre', slot.trimestre)
          .eq('semana', slot.semana)
          .eq('dia_semana', slot.dia_semana)
          .eq('escopo_jornada', escopoJornada)
          .in('modalidade', mods)
          .in('refeicao', refeicoes)
        if (err) throw new Error(err.message)
      }
      setInfo('Cardápio removido.')
      await marcarDiasComCardapio()
      await loadCardapiosDia()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao limpar cardápio')
    } finally {
      setApplying(false)
    }
  }

  function openModalApply(modo: AplicarCardapioModo) {
    if (modo === 'lote' && !selectedDates.length) {
      setError('Selecione ao menos um dia letivo.')
      return
    }
    if (modo === 'dia' && !focusedDate) {
      setError('Selecione um dia no calendário.')
      return
    }
    setApplyModo(modo)
    setModalApplyOpen(true)
  }

  function confirmModalApply(escolhas: AplicarRefeicaoEscolha[]) {
    const dates = applyModo === 'lote' ? selectedDates : focusedDate ? [focusedDate] : []
    void aplicarComRefeicoes(escolhas, dates)
  }

  function removerPrato(refeicao: RefeicaoFicha, pratoId: string) {
    setSecoes((prev) =>
      prev.map((s) => {
        if (s.refeicao !== refeicao) return s
        return {
          ...s,
          pratoIds: s.pratoIds.filter((id) => id !== pratoId),
          itens: s.itens.filter((it) => it.origem_prato_id !== pratoId),
        }
      }),
    )
  }

  async function excluirFichaTecnica(fichaId: string, modalidade: ModalidadeEnsino, refeicao: string) {
    if (!canExcluirFicha) return
    const { count, error: countErr } = await supabase
      .from('cardapios')
      .select('id', { count: 'exact', head: true })
      .eq('modalidade', modalidade)
      .eq('refeicao', refeicao)
      .eq('escopo_jornada', escopoJornada)
      .neq('descricao', '')
    if (countErr) {
      setError(countErr.message)
      return
    }
    const n = count || 0
    const msg =
      n > 0
        ? `Tem certeza de que deseja remover esta ficha técnica? Essa ação desvinculará a refeição de planejamentos associados.\n\nHá ${n} dia(s) com cardápio para esta modalidade × refeição — o cardápio desses slots será limpo.`
        : 'Tem certeza de que deseja remover esta ficha técnica? Essa ação desvinculará a refeição de planejamentos associados.'
    if (!window.confirm(msg)) return
    setError(null)
    setInfo(null)
    try {
      if (n > 0) {
        const { error: clearErr } = await supabase
          .from('cardapios')
          .delete()
          .eq('modalidade', modalidade)
          .eq('refeicao', refeicao)
          .eq('escopo_jornada', escopoJornada)
        if (clearErr) throw new Error(clearErr.message)
      }
      const { error: delErr } = await supabase.from('fichas_tecnicas').delete().eq('id', fichaId)
      if (delErr) throw new Error(delErr.message)
      setInfo('Ficha técnica removida.')
      if (tab === 'ficha') await loadFicha()
      await marcarDiasComCardapio()
      await loadCardapiosDia()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir ficha')
    }
  }

  async function saveFicha() {
    if (!writable) return
    setSaving(true)
    setError(null)
    setInfo(null)
    try {
      await salvarFichaTecnicaCompleta(
        grupo.modalidades,
        montarSecoesFichaPayload(secoes),
        escopoJornada,
      )
      setInfo(
        grupo.id === 'infantil'
          ? 'Ficha técnica salva (Creche e Pré).'
          : 'Ficha técnica salva.',
      )
      await loadFicha()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao salvar ficha')
    } finally {
      setSaving(false)
    }
  }

  function metaTexto(key: keyof NutrientesTotais): string {
    if (!referencia) return '—'
    if (key === 'carboidratos_g') {
      return `${formatNumber(Number(referencia.carboidratos_g_min), 1)}–${formatNumber(Number(referencia.carboidratos_g_max), 1)}`
    }
    if (key === 'proteinas_g') {
      return `${formatNumber(Number(referencia.proteinas_g_min), 1)}–${formatNumber(Number(referencia.proteinas_g_max), 1)}`
    }
    if (key === 'lipideos_g') {
      return `${formatNumber(Number(referencia.lipideos_g_min), 1)}–${formatNumber(Number(referencia.lipideos_g_max), 1)}`
    }
    return formatNumber(Number(referencia[key]), 1)
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ficha técnica</h1>
          <p className="kpi-sub" style={{ margin: 0 }}>
            Monte o pacote do turno, aplique o cardápio nos dias letivos e gerencie pratos.
          </p>
        </div>
      </div>

      <div className="cardapio-ref-tabs" role="tablist" style={{ marginBottom: 16 }}>
        <button
          type="button"
          role="tab"
          className={`cardapio-ref-tab${tab === 'calendario' ? ' active' : ''}`}
          aria-selected={tab === 'calendario'}
          onClick={() => setTab('calendario')}
        >
          Calendário &amp; Detalhes do dia
        </button>
        <button
          type="button"
          role="tab"
          className={`cardapio-ref-tab${tab === 'catalogo' ? ' active' : ''}`}
          aria-selected={tab === 'catalogo'}
          onClick={() => setTab('catalogo')}
        >
          Catálogo de Fichas Técnicas
        </button>
        <button
          type="button"
          role="tab"
          className={`cardapio-ref-tab${tab === 'ficha' ? ' active' : ''}`}
          aria-selected={tab === 'ficha'}
          onClick={() => setTab('ficha')}
        >
          Ficha / Pacote
        </button>
        <button
          type="button"
          role="tab"
          className={`cardapio-ref-tab${tab === 'pratos' ? ' active' : ''}`}
          aria-selected={tab === 'pratos'}
          onClick={() => setTab('pratos')}
        >
          Pratos Cadastrados
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {info && <div className="alert alert-success">{info}</div>}
      {(tab === 'ficha' || tab === 'calendario') && avisoIndisponiveis && (
        <div className="alert alert-warning">{avisoIndisponiveis}</div>
      )}

      {tab === 'pratos' ? (
        <PratosSection onError={setError} onInfo={setInfo} />
      ) : tab === 'catalogo' ? (
        <FichaCatalogoTab
          onError={setError}
          onInfo={setInfo}
          onAjustar={irParaAjusteCatalogo}
          onNova={() => {
            setTab('ficha')
            setInfo('Preencha a refeição desejada no pacote e clique em Salvar ficha.')
          }}
        />
      ) : loading ? (
        <div className="loading">Carregando ficha…</div>
      ) : (
        <>
          <div className="filters" style={{ marginBottom: 16 }}>
            <div className="field">
              <label>Modalidade</label>
              <select
                value={grupoId}
                disabled={saving || applying}
                onChange={(e) => setGrupoId(e.target.value as GrupoId)}
              >
                {GRUPOS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Turno / pacote</label>
              <select
                value={turno}
                disabled={saving || applying}
                onChange={(e) => setTurno(e.target.value as TurnoReferencia)}
              >
                {TURNOS_REFERENCIA.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label} — {t.pacote}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: writable || writableCardapio ? 'pointer' : 'default',
                }}
              >
                <input
                  type="checkbox"
                  checked={integralIndependente}
                  disabled={togglingIntegral || (!writable && !writableCardapio)}
                  onChange={(e) => void toggleIntegralIndependente(e.target.checked)}
                />
                Cardápio integral independente
              </label>
              <p className="kpi-sub" style={{ margin: '4px 0 0' }}>
                {integralIndependente
                  ? 'Integral usa ficha/cardápio próprios (não compartilha Manhã/Tarde/Noite).'
                  : 'Integral compartilha as refeições dos turnos parciais (padrão).'}
              </p>
            </div>
            {tab === 'ficha' && writable && (
              <div className="field" style={{ alignSelf: 'end', display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving}
                  onClick={() => void saveFicha()}
                >
                  {saving ? 'Salvando…' : 'Salvar ficha'}
                </button>
              </div>
            )}
          </div>

          {tab === 'calendario' && (
            <section className="card" style={{ marginBottom: 24 }}>
              <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>Calendário e cardápio por dia</h2>
              <FichaCalendarioDetalhes
                mesLabel={`${MES_LABELS[month]} ${year}`}
                grupoLabel={grupo.label}
                turnoLabel={turnoMeta?.label || turno}
                nutricionistaLabel={nutricionistaResponsavel}
                cells={cells}
                diasComCardapio={diasComCardapio}
                selectedDates={selectedDates}
                focusedDate={focusedDate}
                cardapiosDia={cardapiosDia}
                loadingCardapioDia={loadingCardapioDia}
                totaisPorSecao={totaisPorSecao}
                writableCardapio={writableCardapio}
                applying={applying}
                selectedSlotsCount={selectedSlots.length}
                onPrevMonth={() => setCursor(new Date(year, month - 1, 1))}
                onNextMonth={() => setCursor(new Date(year, month + 1, 1))}
                onFocusDia={focarDia}
                onToggleFocusedInBatch={toggleFocusedInBatch}
                onSelecionarTodosLetivos={selecionarTodosLetivos}
                onLimparSelecao={limparSelecao}
                onOpenApply={openModalApply}
                onClearDay={() => {
                  if (!focusedDate) return
                  if (!window.confirm('Excluir todo o cardápio deste dia (todas as refeições do turno)?')) return
                  void limparCardapioSlots([focusedDate])
                }}
                onClearLote={() => {
                  if (!selectedDates.length) return
                  if (
                    !window.confirm(
                      `Limpar cardápio de ${selectedDates.length} dia(s) selecionado(s) para este turno?`,
                    )
                  )
                    return
                  void limparCardapioSlots(selectedDates)
                }}
                onRemoveRefeicaoDia={(refeicao) => {
                  if (!focusedDate) return
                  if (!window.confirm(`Remover ${labelRefeicao(refeicao)} deste dia?`)) return
                  void limparCardapioSlots([focusedDate], [refeicao])
                }}
              />
            </section>
          )}

          {tab === 'ficha' && (
            <>
              {secoes.map((secao) => {
                const disponiveis = pratos.filter((p) => p.refeicao === secao.refeicao)
                const sub = totaisPorSecao.get(secao.refeicao)
                return (
                  <section key={secao.refeicao} className="card" style={{ marginBottom: 16 }}>
                    <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>{labelRefeicao(secao.refeicao)}</h2>
                    <div className="field" style={{ marginBottom: 12 }}>
                      <label>Descritivo da preparação</label>
                      <textarea
                        rows={2}
                        value={secao.descritivo}
                        disabled={!writable || saving}
                        onChange={(e) =>
                          setSecoes((prev) =>
                            prev.map((s) =>
                              s.refeicao === secao.refeicao ? { ...s, descritivo: e.target.value } : s,
                            ),
                          )
                        }
                        placeholder="Opcional — texto livre da preparação"
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div className="filters" style={{ marginBottom: 12, alignItems: 'end' }}>
                      <div className="field" style={{ minWidth: 260 }}>
                        <label>Adicionar prato…</label>
                        <select
                          value=""
                          disabled={!writable || saving || !disponiveis.length}
                          onChange={(e) => {
                            adicionarPrato(secao.refeicao, e.target.value)
                            e.target.value = ''
                          }}
                        >
                          <option value="">
                            {disponiveis.length
                              ? 'Selecionar prato para copiar…'
                              : 'Nenhum prato nesta refeição'}
                          </option>
                          {disponiveis.map((p) => {
                            const nut = nutrientesPrato(p.id)
                            return (
                              <option key={p.id} value={p.id}>
                                {p.nome} ({formatNumber(nut.energia_kcal, 0)} kcal)
                              </option>
                            )
                          })}
                        </select>
                      </div>
                      {writable && (
                        <div className="field">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={saving}
                            onClick={() => addItemManual(secao.refeicao)}
                          >
                            Adicionar alimento
                          </button>
                        </div>
                      )}
                    </div>

                    {secao.pratoIds.length > 0 && (
                      <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {secao.pratoIds.map((pratoId) => {
                          const p = pratos.find((x) => x.id === pratoId)
                          return (
                            <div
                              key={pratoId}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                                padding: '8px 10px',
                                border: '1px solid var(--color-border)',
                                borderRadius: 8,
                                background: '#fff',
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{p?.nome || 'Prato'}</span>
                              {writable && (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  disabled={saving}
                                  onClick={() => removerPrato(secao.refeicao, pratoId)}
                                >
                                  Remover
                                </button>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    {canExcluirFicha && secao.fichaId && (
                      <div style={{ marginBottom: 12 }}>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          disabled={saving}
                          onClick={() =>
                            void excluirFichaTecnica(secao.fichaId!, grupo.leitura, secao.refeicao)
                          }
                        >
                          Excluir ficha desta refeição
                        </button>
                      </div>
                    )}

                    {!secao.itens.length ? (
                      <p className="kpi-sub">
                        Nenhum alimento nesta refeição. Selecione um prato ou acrescente alimentos.
                      </p>
                    ) : (
                      <div className="table-wrap" style={{ marginBottom: 8 }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Alimento</th>
                              <th className="col-num" style={{ width: 100 }}>
                                Qtd (g)
                              </th>
                              <th className="col-num">kcal</th>
                              <th className="col-num">PTN</th>
                              <th className="col-num">CHO</th>
                              <th className="col-num">LIP</th>
                              <th>Origem</th>
                              {writable && <th style={{ width: 72 }} />}
                            </tr>
                          </thead>
                          <tbody>
                            {secao.itens.map((it) => {
                              const alimento = alimentosById.get(it.alimento_id)
                              const indisponivel = it.alimento_id && !alimentoComTabelaPrecos(alimento)
                              const opcoes = opcoesAlimentoFicha(
                                alimentosCatalogo,
                                it.alimento_id,
                                alimentosById,
                              )
                              const nut = nutrientesDaPorcao(
                                alimento,
                                Number(it.quantidade_g) || 0,
                              )
                              const origemNome = it.origem_prato_id
                                ? pratos.find((p) => p.id === it.origem_prato_id)?.nome
                                : null
                              return (
                                <tr key={it.key}>
                                  <td>
                                    <select
                                      value={it.alimento_id}
                                      disabled={!writable || saving}
                                      onChange={(e) =>
                                        updateItem(secao.refeicao, it.key, {
                                          alimento_id: e.target.value,
                                        })
                                      }
                                      style={{ width: '100%', minWidth: 180 }}
                                    >
                                      <option value="">Selecione…</option>
                                      {opcoes.map((a) => (
                                        <option
                                          key={a.id}
                                          value={a.id}
                                          disabled={
                                            !alimentoComTabelaPrecos(a) && a.id !== it.alimento_id
                                          }
                                        >
                                          {a.nome}
                                          {!alimentoComTabelaPrecos(a) ? ' (indisponível)' : ''}
                                        </option>
                                      ))}
                                    </select>
                                    {indisponivel && (
                                      <p
                                        className="kpi-sub"
                                        style={{
                                          margin: '4px 0 0',
                                          color: 'var(--color-warning, #b45309)',
                                        }}
                                      >
                                        Indisponível na Tabela de Preços
                                      </p>
                                    )}
                                  </td>
                                  <td className="col-num">
                                    <input
                                      type="number"
                                      min={0}
                                      step={0.1}
                                      value={it.quantidade_g}
                                      disabled={!writable || saving}
                                      onChange={(e) =>
                                        updateItem(secao.refeicao, it.key, {
                                          quantidade_g: e.target.value,
                                        })
                                      }
                                      style={{ width: 88, textAlign: 'right' }}
                                    />
                                  </td>
                                  <td className="col-num">{formatNumber(nut.energia_kcal, 1)}</td>
                                  <td className="col-num">{formatNumber(nut.proteinas_g, 1)}</td>
                                  <td className="col-num">{formatNumber(nut.carboidratos_g, 1)}</td>
                                  <td className="col-num">{formatNumber(nut.lipideos_g, 1)}</td>
                                  <td className="kpi-sub">{origemNome || 'Manual'}</td>
                                  {writable && (
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-ghost"
                                        disabled={saving}
                                        onClick={() => removeItem(secao.refeicao, it.key)}
                                        aria-label="Remover alimento"
                                      >
                                        Remover
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="kpi-sub">
                      Subtotal: {formatNumber(sub?.energia_kcal || 0, 1)} kcal · PTN{' '}
                      {formatNumber(sub?.proteinas_g || 0, 1)} · CHO{' '}
                      {formatNumber(sub?.carboidratos_g || 0, 1)} · LIP{' '}
                      {formatNumber(sub?.lipideos_g || 0, 1)}
                    </div>
                  </section>
                )
              })}

              <section className="card ficha-comparacao">
                <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>
                  Total do pacote — {turnoMeta?.label} ({turnoMeta?.pacote})
                </h2>
                <div className="table-wrap">
                  <table className="data-table data-table-valores-ref">
                    <thead>
                      <tr>
                        <th>Nutriente</th>
                        <th className="col-num">Total ficha</th>
                        <th className="col-num">Meta referência</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {NUTRI_COLS.map((c) => {
                        const st = comparacao[c.key]
                        return (
                          <tr key={c.key}>
                            <td>{c.label}</td>
                            <td className="col-num">{formatNumber(totalPacote[c.key], 2)}</td>
                            <td className="col-num">{metaTexto(c.key)}</td>
                            <td>
                              <span className={`ficha-cmp-badge ${statusClass(st)}`}>
                                {statusLabel(st)}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          <ModalAplicarCardapio
            open={modalApplyOpen}
            modo={applyModo}
            alvoLabel={
              applyModo === 'lote'
                ? `${selectedDates.length} dia(s) selecionado(s)`
                : focusedDate
                  ? focusedDate.split('-').reverse().join('/')
                  : '—'
            }
            turnoLabel={turnoMeta?.label || turno}
            secoes={secoes}
            pratos={pratos}
            alimentosById={alimentosById}
            applying={applying}
            onClose={() => setModalApplyOpen(false)}
            onConfirm={confirmModalApply}
          />
        </>
      )}
    </div>
  )
}
